import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { rawApi } from '../../api/client';
import { currencySymbol, formatMoney, toMinorUnits } from '../../lib/format';
import { notifyDone, notifyError, notifySaved } from '../../lib/notify';

/**
 * One regular's account: what they owe (or have left), visit by visit, and the two things only the
 * owner decides — whether they may charge at all, and how much they may owe.
 *
 * THE TWO CURRENCIES NEVER MEET. A dollar debt is a dollar debt and a lempira prepayment is a
 * lempira prepayment; every figure on this screen carries its own symbol and nothing sums them.
 */

interface Money {
  currency: 'USD' | 'HNL';
  minor: number;
}

interface AccountCharge {
  id: string;
  at: string;
  amount: Money;
  coveredMinor: number;
  settled: boolean;
  factura: { documentId: string; correlativo: string | null } | null;
}

interface AccountPayment {
  id: string;
  at: string;
  kind: 'register' | 'transfer';
  amount: Money;
  reference: string | null;
}

interface CustomerAccount {
  customer: { id: string; name: string; status: string; externalRef: string | null };
  creditAllowed: boolean;
  creditLimits: Money[];
  balances: Money[];
  aging: { currency: 'USD' | 'HNL'; owedThisWeekMinor: number; owedOlderMinor: number }[];
  charges: AccountCharge[];
  payments: AccountPayment[];
}

/** The signed balance as a person says it. Positive is theirs; negative is owed to the bar. */
function balanceSentence(balance: Money): { text: string; color: string } {
  if (balance.minor < 0) {
    return { text: `Owes ${formatMoney(-balance.minor, balance.currency)}`, color: 'red' };
  }
  return { text: `${formatMoney(balance.minor, balance.currency)} on their tab`, color: 'teal' };
}

const day = (at: string) => at.slice(0, 10);

export function CustomerAccountModal({
  customerId,
  name,
  onClose,
}: {
  customerId: string;
  name: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['customer-account', customerId];
  const account = useQuery({
    queryKey,
    queryFn: () => rawApi<CustomerAccount>('GET', `/admin/v1/customers/${customerId}/account`),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey });

  return (
    <Modal opened onClose={onClose} title={<Title order={4}>{name}&rsquo;s account</Title>} size="xl">
      {account.isLoading && <Loader />}
      {account.isError && <Alert color="red">This account could not be read. Close and try again.</Alert>}
      {account.data && (
        <Stack gap="lg">
          <BalanceSummary account={account.data} />
          <CreditControls account={account.data} customerId={customerId} refresh={refresh} />
          <TransferForm customerId={customerId} name={name} refresh={refresh} />
          <ChargesTable charges={account.data.charges} />
          <PaymentsTable payments={account.data.payments} />
        </Stack>
      )}
    </Modal>
  );
}

function BalanceSummary({ account }: { account: CustomerAccount }) {
  if (account.balances.length === 0) {
    return <Text c="dimmed">Nothing on the book — no money owed, and nothing prepaid.</Text>;
  }
  return (
    <Stack gap="xs">
      <Group gap="md">
        {account.balances.map((balance) => {
          const said = balanceSentence(balance);
          return (
            <Badge key={balance.currency} size="lg" color={said.color} variant="light">
              {said.text}
            </Badge>
          );
        })}
      </Group>
      {account.aging.length > 0 && (
        <Group gap="lg">
          {account.aging.map((bucket) => (
            <Text key={bucket.currency} size="sm" c="dimmed">
              {bucket.owedThisWeekMinor > 0 &&
                `This week: ${formatMoney(bucket.owedThisWeekMinor, bucket.currency)}`}
              {bucket.owedThisWeekMinor > 0 && bucket.owedOlderMinor > 0 && ' · '}
              {bucket.owedOlderMinor > 0 && `Older: ${formatMoney(bucket.owedOlderMinor, bucket.currency)}`}
            </Text>
          ))}
        </Group>
      )}
    </Stack>
  );
}

/** The owner's two decisions: the switch, and the cap per currency. */
function CreditControls({
  account,
  customerId,
  refresh,
}: {
  account: CustomerAccount;
  customerId: string;
  refresh: () => void;
}) {
  const [creditAllowed, setCreditAllowed] = useState(account.creditAllowed);
  const limitOf = (currency: 'USD' | 'HNL') =>
    account.creditLimits.find((limit) => limit.currency === currency)?.minor;
  const [usdLimit, setUsdLimit] = useState<number | string>(
    limitOf('USD') !== undefined ? (limitOf('USD') as number) / 100 : '',
  );
  const [hnlLimit, setHnlLimit] = useState<number | string>(
    limitOf('HNL') !== undefined ? (limitOf('HNL') as number) / 100 : '',
  );
  useEffect(() => {
    setCreditAllowed(account.creditAllowed);
  }, [account.creditAllowed]);

  const save = useMutation({
    mutationFn: () =>
      rawApi('PATCH', `/admin/v1/customers/${customerId}/account/credit`, {
        creditAllowed,
        creditLimitUsdMinor: usdLimit === '' ? null : toMinorUnits(usdLimit),
        creditLimitHnlMinor: hnlLimit === '' ? null : toMinorUnits(hnlLimit),
      }),
    onSuccess: () => {
      refresh();
      notifySaved(
        creditAllowed
          ? `${account.customer.name} can put bills on their account.`
          : `${account.customer.name} can no longer charge to an account.`,
      );
    },
    onError: (err) => notifyError(err, 'save the credit settings'),
  });

  return (
    <Stack gap="xs">
      <Divider label="Credit" labelPosition="left" />
      <Switch
        checked={creditAllowed}
        onChange={(e) => setCreditAllowed(e.currentTarget.checked)}
        label="Let them put bills on their account and settle later"
        description="With this off the register never offers it, whatever the limits below say. Prepaying is always allowed — that is their own money."
      />
      <Group gap="md" align="end">
        <NumberInput
          label="Most they may owe in lempiras"
          description="Empty means no cap"
          prefix={`${currencySymbol('HNL')} `}
          min={0}
          value={hnlLimit}
          onChange={setHnlLimit}
          w={220}
        />
        <NumberInput
          label="Most they may owe in dollars"
          description="Empty means no cap"
          prefix={`${currencySymbol('USD')} `}
          min={0}
          value={usdLimit}
          onChange={setUsdLimit}
          w={220}
        />
        <Button onClick={() => save.mutate()} loading={save.isPending}>
          Save credit settings
        </Button>
      </Group>
    </Stack>
  );
}

/** A transfer that arrived at the bank: recorded here because no drawer ever saw it. */
function TransferForm({
  customerId,
  name,
  refresh,
}: {
  customerId: string;
  name: string;
  refresh: () => void;
}) {
  const [currency, setCurrency] = useState<'HNL' | 'USD'>('HNL');
  const [amount, setAmount] = useState<number | string>('');
  const [reference, setReference] = useState('');

  const record = useMutation({
    mutationFn: () =>
      rawApi<{ appliedMinor: number; creditedMinor: number }>(
        'POST',
        `/admin/v1/customers/${customerId}/account/payments`,
        {
          amountMinor: toMinorUnits(amount),
          currency,
          ...(reference.trim() ? { reference: reference.trim() } : {}),
        },
      ),
    onSuccess: (result) => {
      refresh();
      setAmount('');
      setReference('');
      const parts = [];
      if (result.appliedMinor > 0) {
        parts.push(`${formatMoney(result.appliedMinor, currency)} paid down what ${name} owed`);
      }
      if (result.creditedMinor > 0) {
        parts.push(`${formatMoney(result.creditedMinor, currency)} is on their tab`);
      }
      notifyDone('Payment recorded', `${parts.join(' and ')}. No factura is produced for this.`);
    },
    onError: (err) => notifyError(err, 'record the payment'),
  });

  return (
    <Stack gap="xs">
      <Divider label="Record a transfer payment" labelPosition="left" />
      <Text size="sm" c="dimmed">
        Money that arrived by bank transfer. It pays their oldest bills first in its own currency; anything
        left over stays on their tab. Cash taken at the bar is recorded at the register, not here.
      </Text>
      <Group gap="md" align="end">
        <SegmentedControl
          value={currency}
          onChange={(value) => setCurrency(value as 'HNL' | 'USD')}
          data={[
            { value: 'HNL', label: 'Lempiras' },
            { value: 'USD', label: 'Dollars' },
          ]}
        />
        <NumberInput
          label="Amount"
          prefix={`${currencySymbol(currency)} `}
          min={0.01}
          value={amount}
          onChange={setAmount}
          w={180}
        />
        <TextInput
          label="Bank reference"
          placeholder="Statement line, so it can be matched later"
          value={reference}
          onChange={(e) => setReference(e.currentTarget.value)}
          w={280}
        />
        <Button
          onClick={() => record.mutate()}
          loading={record.isPending}
          disabled={toMinorUnits(amount) === undefined || (toMinorUnits(amount) ?? 0) <= 0}
        >
          Record payment
        </Button>
      </Group>
    </Stack>
  );
}

function ChargesTable({ charges }: { charges: AccountCharge[] }) {
  return (
    <Stack gap="xs">
      <Divider label="Charges" labelPosition="left" />
      {charges.length === 0 ? (
        <Text c="dimmed" size="sm">
          No visits have been charged to this account.
        </Text>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Day</Table.Th>
              <Table.Th>Amount</Table.Th>
              <Table.Th>Factura</Table.Th>
              <Table.Th>Standing</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {charges.map((charge) => (
              <Table.Tr key={charge.id}>
                <Table.Td>{day(charge.at)}</Table.Td>
                <Table.Td>{formatMoney(charge.amount.minor, charge.amount.currency)}</Table.Td>
                <Table.Td>
                  {charge.factura?.correlativo ?? (
                    <Text c="dimmed" span size="sm">
                      Not printed yet
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {charge.settled ? (
                    <Badge color="teal" variant="light">
                      Paid
                    </Badge>
                  ) : charge.coveredMinor > 0 ? (
                    <Badge color="yellow" variant="light">
                      {formatMoney(charge.amount.minor - charge.coveredMinor, charge.amount.currency)} still
                      owed
                    </Badge>
                  ) : (
                    <Badge color="red" variant="light">
                      Owed
                    </Badge>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

function PaymentsTable({ payments }: { payments: AccountPayment[] }) {
  return (
    <Stack gap="xs">
      <Divider label="Payments" labelPosition="left" />
      {payments.length === 0 ? (
        <Text c="dimmed" size="sm">
          No payments yet.
        </Text>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Day</Table.Th>
              <Table.Th>Amount</Table.Th>
              <Table.Th>How</Table.Th>
              <Table.Th>Reference</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {payments.map((payment) => (
              <Table.Tr key={payment.id}>
                <Table.Td>{day(payment.at)}</Table.Td>
                <Table.Td>{formatMoney(payment.amount.minor, payment.amount.currency)}</Table.Td>
                <Table.Td>{payment.kind === 'register' ? 'At the register' : 'Bank transfer'}</Table.Td>
                <Table.Td>{payment.reference ?? ''}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
